import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const HEBREW_DAYS = [
  'א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ז׳', 'ח׳', 'ט׳', 'י׳',
  'י"א', 'י"ב', 'י"ג', 'י"ד', 'ט"ו', 'ט"ז', 'י"ז', 'י"ח', 'י"ט', 'כ׳',
  'כ"א', 'כ"ב', 'כ"ג', 'כ"ד', 'כ"ה', 'כ"ו', 'כ"ז', 'כ"ח', 'כ"ט'
] as [string, ...string[]];
const HEBREW_MONTHS = [
  'ניסן', 'אייר', 'סיון', 'תמוז', 'אב', 'אלול',
  'תשרי', 'חשון', 'כסלו', 'טבת', 'שבט', 'אדר', 'אדר ב׳'
] as [string, ...string[]];

const yahrzeitFormSchema = z.object({
  name: z.string().min(1, { message: 'שם הוא שדה חובה' }),
  gender: z.enum(['male', 'female'], { required_error: 'יש לבחור בן או בת' }),
  father_name: z.string().optional(),
  gregorian_date: z.date({ required_error: 'יש לבחור תאריך' }),
  hebrew_day: z.enum(HEBREW_DAYS, { required_error: 'יש לבחור יום עברי' }),
  hebrew_month: z.enum(HEBREW_MONTHS, { required_error: 'יש לבחור חודש עברי' }),
  notes: z.string().optional(),
});

interface YahrzeitFormProps {
  onSubmit: (values: z.infer<typeof yahrzeitFormSchema>) => void;
  onCancel: () => void;
  initialValues?: z.infer<typeof yahrzeitFormSchema>;
}

const YahrzeitForm: React.FC<YahrzeitFormProps> = ({
  onSubmit,
  onCancel,
  initialValues
}) => {
  const form = useForm<z.infer<typeof yahrzeitFormSchema>>({
    resolver: zodResolver(yahrzeitFormSchema),
    defaultValues: initialValues || {
      name: '',
      gender: 'male',
      father_name: '',
      gregorian_date: undefined,
      hebrew_day: 'א׳',
      hebrew_month: 'ניסן',
      notes: '',
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>שם הנפטר/ת</FormLabel>
                <FormControl>
                  <Input placeholder="הזן שם מלא בעברית" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="father_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>שם האב</FormLabel>
                <div className="flex gap-4 items-start">
                  <div className="flex-1">
                    <FormControl>
                      <Input placeholder="הזן שם האב" {...field} />
                    </FormControl>
                  </div>
                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <FormLabel>בן/בת</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className="flex gap-4"
                          >
                            <FormItem className="flex items-center space-x-2 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="male" />
                              </FormControl>
                              <FormLabel className="font-normal">בן</FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center space-x-2 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="female" />
                              </FormControl>
                              <FormLabel className="font-normal">בת</FormLabel>
                            </FormItem>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="gregorian_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>תאריך פטירה לועזי</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? format(field.value, "dd/MM/yyyy") : "בחר תאריך"}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      disabled={(date) => date > new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="hebrew_day"
            render={({ field }) => (
              <FormItem>
                <FormLabel>יום עברי</FormLabel>
                <FormControl>
                  <select {...field} className="form-select w-full">
                    {HEBREW_DAYS.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="hebrew_month"
            render={({ field }) => (
              <FormItem>
                <FormLabel>חודש עברי</FormLabel>
                <FormControl>
                  <select {...field} className="form-select w-full">
                    {HEBREW_MONTHS.map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>הערות נוספות (אופציונלי)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="מידע נוסף שברצונך לציין"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onCancel} type="button">בטל</Button>
          <Button type="submit">שמור</Button>
        </div>
      </form>
    </Form>
  );
};

export default YahrzeitForm;
